import React, { type ReactNode } from 'react';

import {
	isSearchConfluencePageBlogAttachment,
	type SearchConfluencePartial,
} from '@atlassian/dynamic-cards-search-client';
import { transformAnalyticsToQueryParam } from '@atlassian/dynamic-cards-search-common';

import { ProductKeys } from '../../../../../../common/constants/products';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { ProductIcon } from '../../../../../../common/ui/product-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { BreadcrumbSocialSignal } from '../../../../../../common/ui/social-signals';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { addQueryParams, getUrl } from '../../../../../../common/utils/url-utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSearchQuery,
	useSearchSessionId,
	useSelectedProduct,
} from '../../../../../../controllers/store';

import { ConfluenceResultMeta } from './metadata/header';
import { CommentCount } from './metadata/header/comment-count';
import { LikeCount } from './metadata/header/like-count';
import { Verified } from './metadata/header/verified';

const getTitle = (partial: SearchConfluencePartial) => {
	return enrichExcerpt(partial.title);
};

const getLastModified = (partial: SearchConfluencePartial) => {
	if (partial.lastModified) {
		return <LastModified lastModified={partial.lastModified} />;
	}
	return null;
};

const getBreadcrumbs = (partial: SearchConfluencePartial) => {
	if (!isSearchConfluencePageBlogAttachment(partial)) {
		return null;
	}

	return <ConfluenceResultMeta partial={partial} />;
};

const getVerifiedIcon = (partial: SearchConfluencePartial) => {
	if (isSearchConfluencePageBlogAttachment(partial) && partial.isVerified) {
		return <Verified />;
	}
	return null;
};

const getCommentCount = (partial: SearchConfluencePartial) => {
	if (!isSearchConfluencePageBlogAttachment(partial)) {
		return null;
	}

	const numberOfComments = partial.confluenceEntity?.commentCountSummary?.total ?? 0;

	if (numberOfComments === 0) {
		return null;
	}

	return <CommentCount numberOfComments={numberOfComments} />;
};

const getLikeCount = (partial: SearchConfluencePartial) => {
	if (!isSearchConfluencePageBlogAttachment(partial)) {
		return null;
	}

	const numberOfLikes = partial.confluenceEntity?.likesSummary?.count ?? 0;

	if (numberOfLikes === 0) {
		return null;
	}

	return <LikeCount numberOfLikes={numberOfLikes} />;
};

const getDescription = (partial: SearchConfluencePartial) => {
	return processExcerpt(partial.description);
};

interface ConfluenceSearchResultProps {
	partial: SearchConfluencePartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const ConfluenceSearchResult = ({ partial, position }: ConfluenceSearchResultProps) => {
	const [{ cloudId, isAdminHubAIEnabled }] = useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [searchSessionId] = useSearchSessionId();
	const [primaryProduct] = usePrimaryProduct();
	const [query] = useSearchQuery();

	const icon = <ProductIcon product={ProductKeys.Confluence} />;

	const metadataParts: [string, ReactNode][] = [
		['type', <SearchResultType type={partial.type} />],
		['breadcrumbs', getBreadcrumbs(partial)],
		['last-modified', getLastModified(partial)],
		['verified', getVerifiedIcon(partial)],
		['comment-count', getCommentCount(partial)],
		['like-count', getLikeCount(partial)],
	];

	if (partial.socialSignal) {
		metadataParts.push(['social-signal', <BreadcrumbSocialSignal partial={partial} />]);
	}

	const appendAdditionalQueryParams = ({ url, resultId }: { resultId: string; url: string }) => {
		let href = getUrl(partial.url, 'search_id', searchSessionId);
		// fg('cc_page_experiences_enhanced_search_analytics_conf') => false
		if (false) {
			href = transformAnalyticsToQueryParam({
				query,
				url,
				searchSessionId,
				resultId,
				addQueryParams,
				addQuery: getUrl,
			});
		}
		return href;
	};

	return (
		<SearchResult
			{...partial}
			primaryProduct={primaryProduct}
			url={appendAdditionalQueryParams({ url: partial.url, resultId: partial.id })}
			title={getTitle(partial)}
			icon={icon}
			headerMetadata={
				<SearchMetadata
					product="confluence"
					productSelected={!!selectedProduct}
					parts={metadataParts}
				/>
			}
			description={getDescription(partial)}
			showAISummary={['page', 'blogpost'].includes(partial.type) && isAdminHubAIEnabled}
			cloudId={cloudId}
			position={position}
			partial={partial}
		/>
	);
};
